import { useStopMentionBoost } from '@/hooks/actions/use-stop-mention-boost';
import { useCallback, useState } from 'react';

export interface BoostStopFlow {
  isConfirmOpen: boolean;
  isStopping: boolean;
  openConfirm: () => void;
  cancelConfirm: () => void;
  confirmStop: () => void;
}

/**
 * Shared confirm-then-stop flow for a launched boost. Both the inline boost
 * panel and the setup pane's read-only launched view render the same stop
 * affordance, so the dialog state, the stop mutation, and the stable handlers
 * live here once instead of being duplicated per surface.
 *
 * The confirm closes only on a successful stop, so a failed stop keeps the
 * dialog open with its error toast; the stop hook reconciles delivery state.
 */
export function useBoostStopFlow(mentionId: string): BoostStopFlow {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { isPending: isStopping, mutate: stopBoost } = useStopMentionBoost();

  const openConfirm = useCallback(() => setIsConfirmOpen(true), []);
  const cancelConfirm = useCallback(() => setIsConfirmOpen(false), []);
  const confirmStop = useCallback(() => {
    stopBoost({ mentionId }, { onSuccess: () => setIsConfirmOpen(false) });
  }, [mentionId, stopBoost]);

  return { isConfirmOpen, isStopping, openConfirm, cancelConfirm, confirmStop };
}
